import { inject, injectable } from "inversify";
import "jaydata/odata";
import autobind from "autobind-decorator";
import { observable, computed } from 'mobx';

import { factory, OData, $data } from './data/JayDataContext'
import { IdentityService } from "./identity.service";
import { ConfigurationService } from "./configuration.service";
import { ObjectUtils } from '../utils/object'

interface ContextResolver {
	resolve: (value: any) => void,
	reject: (reason?: any) => void,
}

@injectable()
export class ContextService {
	private ctx: OData.Container;
	private ctxResolver: ContextResolver[];

	public identityService: IdentityService;
	public configurationService: ConfigurationService;

	@observable
	private _isDirty = false;
	@computed
	public get isDirty() {
		return this._isDirty;
	};

	constructor(@inject(IdentityService) identityService, @inject(ConfigurationService) configurationService) {
		this.configurationService = configurationService;
		this.identityService = identityService;

		// http://jaydata.org/blog/groundbreaking-odata-v4-features-in-jaydata-1.5.1-ctp#wptoc_0_0_3
		// Deep Insert (OData)
		//(<any>$data).defaults.OData.enableDeepSave = true;

		// Enable Entity Rerefences (OData)
		(<any>$data).defaults.OData.withReferenceMethods = true;

		this.ctxResolver = [];
		var self = this;
		(<any>$data.EntityContext.prototype).prepareRequest = function (r) {
			var rUri = r[0].requestUri;
			if ((<any>$data.EntityContext.prototype).searchKeywords) {
				if (rUri.indexOf('?') > -1) {
					r[0].requestUri = r[0].requestUri + "&$search=" + (<any>$data.EntityContext.prototype).searchKeywords;
				} else {
					r[0].requestUri = r[0].requestUri + "?$search=" + (<any>$data.EntityContext.prototype).searchKeywords;
				}
			}
			if ((<any>$data.EntityContext.prototype).params) {
				const params: { [key: string]: any } = (<any>$data.EntityContext.prototype).params;
				for (const param of Object.getOwnPropertyNames(params)) {
					const value = params[param];
					if (rUri.indexOf('?') > -1) {
						r[0].requestUri = `${r[0].requestUri}&${param}=${value}`;
					} else {
						r[0].requestUri = `${r[0].requestUri}?${param}=${value}`;
					}
				}
			}
			if (r[0].data && r[0].data.dynamicProperties) {
				for (let key in r[0].data.dynamicProperties) {
					r[0].data[key] = r[0].data.dynamicProperties[key];
				}
				delete r[0].data.dynamicProperties;
			}
			r[0].data = ObjectUtils.deepCopy(r[0].data, attr => attr == '__isProxy' || attr == '__edit');
			r[0].headers['Authorization'] = `Bearer ${self.identityService.accessToken}`;
			r[0].headers['Cache-Control'] = 'no-cache';
		};
		var fail = (reason) => {
			for (let idx = 0; idx < this.ctxResolver.length; idx++)
				this.ctxResolver[idx].reject(reason);
		};
		this.configurationService.current().then(config => {
			factory({
				oDataServiceHost: `${config.serviceUrl}odata`,
			}).onReady().then(ctx => {
				self.ctx = ctx;
				(<any>ctx).addEventListener('added', self.resetState);
				(<any>ctx).addEventListener('updated', self.resetState);
				(<any>ctx).addEventListener('deleted', self.resetState);
				for (let idx = 0; idx < this.ctxResolver.length; idx++)
					this.ctxResolver[idx].resolve(ctx);
				this.ctxResolver = [];
			}).catch(fail);
		}).catch(fail);
	}

	@autobind
	public current(): Promise<OData.Container> {
		if (this.ctx) {
			return Promise.resolve(this.ctx);
		}
		var self = this;
		return new Promise((resolve, reject) => {
			this.ctxResolver.push(<ContextResolver>{ resolve: resolve, reject: reject });
		});
	}

	@autobind
	public resetChanges() {
		this.current().then(context => {
			context.stateManager.reset();
			this.resetState();
		})
	}

	public registerEntityChanges(items: $data.Entity[] | $data.Entity, isDirty?: boolean) {
		if (isDirty != null) {
			this._isDirty = true;
		}
		if (items == null) return;
		if (Array.isArray(items)) {
			for (let entity of items) {
				this.addEventHandler(entity);
			}
		} else {
			this.addEventHandler(items);
		}
	}

	private addEventHandler(entity: $data.Entity) {
		entity.propertyChanged.attach(this.setDirty);
	}

	@autobind
	private setDirty(sender: $data.Entity, event: Event) {
		if (
			event.propertyName === "dynamicProperties"
			|| event.newValue instanceof Date
			|| event.newValue instanceof $data.Geography
			|| typeof event.newValue != "object"
			|| (event.newValue == null && event.oldValue != null)
		) {
			if (event.newValue != event.oldValue) {
				this._isDirty = true;
			}
		}
	}

	@autobind
	private resetState() {
		this._isDirty = false;
	}
}

interface Event {
	eventName: string;
	newValue: any;
	oldValue: any;
	propertyName: string;
}



// WEBPACK FOOTER //
// ./src/core/services/context.service.ts